import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ChildrenOutletContexts, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MENU_ITEMS } from 'Core/constants';
import { getUserRoutes } from 'Core/helpers';
import { IMenuItem } from 'Core/interfaces';
import { AppRoutes } from 'Core/routes';
import { CurrentUserStateService } from 'Core/services';
import { isNil } from 'lodash';
import { NgxPermissionsService } from 'ngx-permissions';
import { filter } from 'rxjs';
import { routeAnimation } from './shell.animation';

@UntilDestroy()
@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [routeAnimation],
  standalone: false,
})
export class ShellComponent implements OnDestroy, OnInit {
  @Input() public hasButton!: boolean;
  @Input() public menu!: IMenuItem[];
  @Input() public buttonText!: string;
  public isCollapsed: boolean = false;
  public headerMenu = MENU_ITEMS;

  constructor(
    private ngxPermissionsService: NgxPermissionsService,
    private currentUserStateService: CurrentUserStateService,
    private router: Router,
    private contexts: ChildrenOutletContexts
  ) {}

  protected getRouteAnimationState(): string {
    return this.contexts.getContext('primary')?.route?.snapshot?.url.toString() ?? '';
  }

  public ngOnInit(): void {
    this.redirectToRoute(this.router.url);
    this.subscribeOnRouteChanges();
  }

  public ngOnDestroy(): void {
    this.ngxPermissionsService.flushPermissions();
  }

  private subscribeOnRouteChanges(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(event => this.redirectToRoute(event.urlAfterRedirects));
  }

  private redirectToRoute(url: string): void {
    if (!this.isShellRootUrl(url)) {
      return;
    }

    const user = this.currentUserStateService.user();

    if (isNil(user)) {
      return;
    }

    this.router.navigateByUrl(`/${getUserRoutes(user)}`, { replaceUrl: true });
  }

  private isShellRootUrl(url: string): boolean {
    const [pathWithoutQuery] = url.split(/[?#]/);
    return pathWithoutQuery.replace(/\/+$/g, '') === `/${AppRoutes.APP}`;
  }
}
